import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Terminal, FileText, Loader2 } from 'lucide-react';
import { marketplace } from '../lib/api';
import { getShowcase } from '../data/showcaseData';
import { useSeo } from '../hooks/useSeo';
import PriceTag from '../components/PriceTag';

/**
 * A single-skill landing page.
 *
 * /marketplace/:slug is the product page — files, price, buy. This is the
 * argument: what the skill is for, what it does, and what it will not do. The
 * prose is authored per skill in showcaseData; every number on the page comes
 * from the live listing so the two can never disagree.
 */

function Section({ eyebrow, title, children, className = '' }) {
  return (
    <section className={`max-w-[820px] mx-auto px-6 ${className}`}>
      {eyebrow && (
        <div className="mono text-[0.68rem] tracking-[0.18em] uppercase text-[var(--text-4)] mb-3">
          {eyebrow}
        </div>
      )}
      {title && (
        <h2 className="serif text-[1.9rem] md:text-[2.3rem] leading-[1.15] tracking-[-0.02em] mb-6">
          {title}
        </h2>
      )}
      {children}
    </section>
  );
}

function Pill({ children, icon: Icon }) {
  return (
    <span className="inline-flex items-center gap-1.5 mono text-[0.68rem] tracking-[0.1em] uppercase px-2.5 py-1 rounded-full border border-[var(--border-2)] text-[var(--text-3)]">
      {Icon && <Icon size={11} className="text-[var(--accent)]" />}
      {children}
    </span>
  );
}

/**
 * The demo, framed as a window rather than dropped on the page as a bare
 * rectangle.
 *
 * The recording already carries its own app chrome, so this adds the frame
 * around it — title bar, rounded body, a warm bloom behind the glass — and
 * nothing that would duplicate what is inside the footage. The glow is one soft
 * accent wash at low opacity; anything stronger reads as a template.
 *
 * A silent looping clip is decoration to a reader who has asked for less
 * motion, so under prefers-reduced-motion it does not autoplay and gains real
 * controls instead.
 */
function DemoWindow({ demo, label }) {
  const ref = useRef(null);
  const [still, setStill] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => {
      setStill(mq.matches);
      const v = ref.current;
      if (v && mq.matches) { v.pause(); v.currentTime = 0; }
    };
    apply();
    mq.addEventListener?.('change', apply);
    return () => mq.removeEventListener?.('change', apply);
  }, []);

  return (
    <figure className="relative max-w-[1000px] mx-auto px-6 mt-20">
      {/* bloom — decorative only, and never intercepts a click */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-10 top-6 bottom-16 blur-[70px] opacity-[0.16]"
        style={{
          background:
            'radial-gradient(60% 55% at 50% 45%, var(--accent) 0%, rgba(184,84,42,0.55) 45%, transparent 78%)',
        }}
      />
      <div
        className="relative rounded-[11px] overflow-hidden bg-[var(--surface)]
                   border border-[var(--border-2)]
                   shadow-[0_1px_0_0_var(--hover-wash)_inset,0_30px_70px_-24px_var(--shadow-deep)]"
      >
        <div className="flex items-center gap-3 h-9 px-4 bg-[var(--surface-2)] border-b border-[var(--border)]">
          <div className="flex items-center gap-[7px]" aria-hidden="true">
            <span className="w-[9px] h-[9px] rounded-full bg-[var(--border-2)]" />
            <span className="w-[9px] h-[9px] rounded-full bg-[var(--border)]" />
            <span className="w-[9px] h-[9px] rounded-full bg-[var(--border)]" />
          </div>
          <span className="mono text-[0.66rem] tracking-[0.08em] text-[var(--text-4)] truncate mx-auto pr-10">
            {label}
          </span>
        </div>
        <video
          ref={ref}
          className="block w-full"
          // Per-demo: the terminal canvas is sized to its own output, so a
          // shared ratio letterboxes one skill and crops another.
          style={{ aspectRatio: demo.aspect || '1280 / 860' }}
          src={demo.src}
          poster={demo.poster}
          autoPlay={!still}
          controls={still}
          muted
          loop
          playsInline
          preload="metadata"
        />
      </div>
      <figcaption className="mt-4 flex gap-3 text-[0.82rem] text-[var(--text-4)] leading-relaxed">
        <span aria-hidden="true" className="text-[var(--accent)] opacity-60">—</span>
        {demo.caption}
      </figcaption>
    </figure>
  );
}

function Code({ children }) {
  return (
    <pre className="bg-[var(--bg-2)] border border-[var(--border)] rounded-sm px-4 py-3 overflow-x-auto">
      <code className="mono text-[0.82rem] text-[var(--text-2)] whitespace-pre">{children}</code>
    </pre>
  );
}

export default function Showcase() {
  const { slug } = useParams();
  const s = getShowcase(slug);
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);

  useSeo({
    title: s ? `${s.name} — ${s.tagline} | Loreto` : 'Loreto',
    description: s?.lede?.slice(0, 180),
    type: 'article',
    image: s?.banner,
  });

  useEffect(() => {
    let alive = true;
    if (!s) { setLoading(false); return; }
    marketplace
      .detail(slug)
      .then((d) => alive && setListing(d))
      .catch(() => alive && setListing(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [slug, s]);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  // An unknown slug is not a broken page, it just has no showcase written for
  // it yet — send it to the product page, which works for every listing.
  if (!s) return <Navigate to={`/marketplace/${slug}`} replace />;

  const files = listing?.files_manifest || [];
  const free = listing && Number(listing.price_usd) === 0;
  const buyHref = `/marketplace/${slug}`;

  return (
    <div className="pb-32">
      {/* ── hero ─────────────────────────────────────────────── */}
      <div className="max-w-[1100px] mx-auto px-6 pt-28 md:pt-32">
        <img
          src={s.banner}
          alt={s.bannerAlt || s.name}
          className="w-full rounded-sm border border-[var(--border)]"
        />
        <div className="mt-10 max-w-[760px]">
          <h1 className="serif text-[2.6rem] md:text-[3.6rem] leading-[1.03] tracking-[-0.025em]">
            {s.tagline}
          </h1>
          <p className="mt-6 text-[1.05rem] leading-[1.65] text-[var(--text-2)]">{s.lede}</p>

          <div className="mt-7 flex flex-wrap items-center gap-2.5">
            {loading ? (
              <Loader2 size={15} className="animate-spin text-[var(--text-4)]" />
            ) : (
              <>
                {listing && <PriceTag usd={listing.price_usd} size="lg" />}
                {listing?.scanned && <Pill icon={ShieldCheck}>Malware scanned</Pill>}
                {files.length > 0 && <Pill icon={FileText}>{files.length} files</Pill>}
                {s.pill && <Pill icon={Terminal}>{s.pill}</Pill>}
              </>
            )}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link to={buyHref} className="btn-primary inline-flex items-center gap-2 whitespace-nowrap">
              {free ? 'Get it free' : 'Get this skill'} <ArrowRight size={15} />
            </Link>
            <Link to={buyHref} className="text-[0.9rem] text-[var(--text-3)] hover:text-[var(--text)] transition-colors">
              See the files and the README →
            </Link>
          </div>
        </div>
      </div>

      {/* ── demo ─────────────────────────────────────────────── */}
      <DemoWindow demo={s.demo} label={s.demo.label || s.run} />

      {/* ── install first, like any tool worth using ─────────── */}
      <Section eyebrow="Install" title={s.headings?.install || 'Install it'} className="mt-24">
        <ol className="space-y-3 mb-6">
          {s.install.steps.map((step, i) => (
            <li key={i} className="flex gap-3 text-[0.98rem] leading-[1.6] text-[var(--text-2)]">
              <span className="mono text-[0.78rem] text-[var(--accent)] pt-1 flex-shrink-0">{i + 1}</span>
              {step}
            </li>
          ))}
        </ol>
        <div className="space-y-3">
          <Code>{`> ${s.install.invoke}`}</Code>
          <Code>{s.install.cli}</Code>
        </div>
        <p className="mt-4 text-[0.86rem] text-[var(--text-3)] leading-relaxed">{s.install.note}</p>
      </Section>

      {/* ── the three checks ─────────────────────────────────── */}
      <Section eyebrow={s.headings?.checksEyebrow || 'What it does'} title={s.headings?.checks || 'How it works'} className="mt-24">
        <div className="space-y-8">
          {s.checks.map((c) => (
            <div key={c.n} className="border-l border-[var(--border-2)] pl-5">
              <div className="mono text-[0.72rem] text-[var(--accent)] mb-1.5">{c.n}</div>
              <h3 className="text-[1.12rem] font-medium mb-2 leading-snug">{c.title}</h3>
              <p className="text-[0.96rem] leading-[1.65] text-[var(--text-2)]">{c.body}</p>
              <p className="mt-2 text-[0.86rem] text-[var(--text-3)] italic">{c.kicker}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ── proof ────────────────────────────────────────────── */}
      <Section eyebrow={s.headings?.proofEyebrow || 'What it found'} title={s.proof.repo} className="mt-24">
        <div className="mono text-[0.74rem] text-[var(--text-4)] mb-4">{s.proof.scale}</div>
        <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <tbody>
              {s.proof.rows.map(([check, module, finding]) => (
                <tr key={check} className="border-b border-[var(--border)] last:border-0">
                  <td className="px-4 py-3 text-[0.86rem] text-[var(--text-3)] align-top whitespace-nowrap">{check}</td>
                  <td className="px-4 py-3 mono text-[0.82rem] text-[var(--accent)] align-top whitespace-nowrap">{module}</td>
                  <td className="px-4 py-3 text-[0.88rem] text-[var(--text-2)] align-top">{finding}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-[0.82rem] text-[var(--text-4)] leading-relaxed">{s.proof.note}</p>
      </Section>

      {/* ── the trap ─────────────────────────────────────────── */}
      <Section eyebrow="Design note" title={s.trap.title} className="mt-24">
        <p className="text-[1rem] leading-[1.7] text-[var(--text-2)]">{s.trap.body}</p>
      </Section>

      {/* ── limits ───────────────────────────────────────────── */}
      <Section eyebrow="Honest limits" title="What it will not do" className="mt-24">
        <ul className="space-y-3">
          {s.limits.map((l, i) => (
            <li key={i} className="flex gap-3 text-[0.96rem] leading-[1.65] text-[var(--text-2)]">
              <span className="text-[var(--text-4)] pt-[3px] flex-shrink-0">—</span>
              {l}
            </li>
          ))}
        </ul>
      </Section>

      {/* ── close ────────────────────────────────────────────── */}
      <Section className="mt-24">
        <div className="border-t border-[var(--border)] pt-10">
          <p className="serif text-[1.5rem] md:text-[1.8rem] leading-[1.3] tracking-[-0.015em] mb-6">
            {s.closing}
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <Link to={buyHref} className="btn-primary inline-flex items-center gap-2 whitespace-nowrap">
              {free ? 'Get it free' : 'Get this skill'} <ArrowRight size={15} />
            </Link>
            {listing?.seller?.username && (
              <Link
                to={`/u/${listing.seller.username}`}
                className="text-[0.9rem] text-[var(--text-3)] hover:text-[var(--text)] transition-colors"
              >
                More from {listing.seller.name || listing.seller.username} →
              </Link>
            )}
          </div>
        </div>
      </Section>
    </div>
  );
}
